import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, CheckCircle, Clock, Eye, EyeOff, Lock, LogIn, Mail, Shield } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Login.css';
import { API_CONFIG } from "../config/api";
import heLogo from '../Assets/HE.AI-Logo.png';

// Base API URL - Using centralized config
const API = API_CONFIG.BASE_URL;

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Get activation parameters from URL if present
    const prefilledEmail = searchParams.get('email');
    const prefilledSchoolId = searchParams.get('school_id');
    const isActivation = searchParams.get('activate') === 'true';

    // Flow control states
    const [stage, setStage] = useState(isActivation ? 'activationSetup' : 'login');
    // 'login' | 'activationSetup' | 'schoolSelection' | 'resetPassword' | 'resetPasswordOtp'

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Form states
    const [email, setEmail] = useState(prefilledEmail || '');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [schoolId, setSchoolId] = useState(prefilledSchoolId || '');
    const [availableSchools, setAvailableSchools] = useState([]);
    const [forgotPasswordSchools, setForgotPasswordSchools] = useState([]);

    // OTP states
    const [otp, setOtp] = useState(Array(6).fill(''));
    const otpRefs = useRef([]);

    // UI control states
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [otpTimeLeft, setOtpTimeLeft] = useState(0);
    const [canResendOtp, setCanResendOtp] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    // Refs for timers
    const otpTimerRef = useRef(null);
    const resendTimerRef = useRef(null);

    // Password strength indicator
    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        match: false
    });

    // Check for existing session on load
    useEffect(() => {
        if (localStorage.getItem('accessToken')) {
            navigate('/HomePage');
        }

        // Clear any existing timers when component unmounts
        return () => {
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);
            if (resendTimerRef.current) clearInterval(resendTimerRef.current);
        };
    }, [navigate]);

    // Update password strength indicators when password changes
    useEffect(() => {
        const newStrength = {
            length: newPassword.length >= 8,
            uppercase: /[A-Z]/.test(newPassword),
            lowercase: /[a-z]/.test(newPassword),
            number: /[0-9]/.test(newPassword),
            special: /[^A-Za-z0-9]/.test(newPassword),
            match: newPassword === confirmPassword && newPassword !== ''
        };
        setPasswordStrength(newStrength);
    }, [newPassword, confirmPassword]);

    // OTP Timer functions
    const startOtpTimer = () => {
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);

        // 10 minutes in seconds
        setOtpTimeLeft(10 * 60);

        otpTimerRef.current = setInterval(() => {
            setOtpTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(otpTimerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Resend countdown timer
    const startResendCountdown = () => {
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);

        setResendCountdown(30);
        setCanResendOtp(false);

        resendTimerRef.current = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(resendTimerRef.current);
                    setCanResendOtp(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Format time remaining as MM:SS
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    // -------------------- API CALLS --------------------

    // 1. INITIAL LOGIN - Try standard login first
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            // Use the OAuth2 form-encoded approach as specified in the guide
            const response = await axios.post(`${API}/login/student`,
                new URLSearchParams({
                    username: email.toLowerCase(),
                    password
                }),
                {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                }
            );

            const data = response.data;

            // Handle different response types
            if (data.status === 'school_selection_required') {
                // Multiple schools - show selection screen
                setAvailableSchools(data.schools);
                setStage('schoolSelection');
            } else if (data.status === 'setup_required') {
                // Account needs activation - go to activation flow
                setSchoolId(data.school_id);
                setStage('activationSetup');
                setSuccessMessage('Your account needs to be activated. Please enter your temporary password.');
            } else if (data.access_token) {
                // Successful login - store tokens and redirect
                handleLoginSuccess(data);
            } else {
                throw new Error('Unexpected response from server');
            }
        } catch (error) {
            console.error('Login error:', error);
            if (error.response?.status === 401) {
                setError('Email or password is incorrect.');
            } else if (error.response?.status === 403) {
                setError('Your account is not active. Please contact the school admin.');
            } else {
                setError(error.response?.data?.detail || error.message || 'Login failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 2. COMPLETE LOGIN after school selection
    const completeLogin = async (selectedSchoolId) => {
        setIsLoading(true);
        setError('');

        try {
            const response = await axios.post(`${API}/login/complete`, {
                email: email.toLowerCase(),
                password,
                role: 'student',
                school_id: selectedSchoolId
            }, {
                headers: { 'Content-Type': 'application/json' }
            });

            const data = response.data;

            // Check if activation is required
            if (data.status === 'setup_required') {
                setSchoolId(selectedSchoolId);
                setStage('activationSetup');
            } else if (data.access_token) {
                handleLoginSuccess(data);
            }
        } catch (error) {
            console.error('Login completion error:', error);
            if (error.response?.status === 403) {
                setError('Account setup required. Please activate your account first.');
                setStage('activationSetup');
            } else {
                setError(error.response?.data?.detail || error.message || 'Login failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 3. ACTIVATION - Verify temp password and set new password (no OTP)
    const handleActivation = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter your email and temporary password');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        const isStrong = passwordStrength.length &&
            passwordStrength.uppercase &&
            passwordStrength.lowercase &&
            passwordStrength.number &&
            passwordStrength.special;

        if (!isStrong) {
            setError('Please choose a stronger password that meets all requirements');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const response = await axios.post(`${API}/student/activate`, {
                email: email.toLowerCase(),
                temp_password: password,
                new_password: newPassword,
                school_id: schoolId
            });

            handleLoginSuccess(response.data);
        } catch (error) {
            console.error('Student activation error:', error);
            const status = error.response?.status;

            if (status === 401) {
                setError('That temporary password doesn\'t match. Try again or contact support.');
            } else if (status === 404) {
                setError('We couldn\'t find that account. Check your email and school.');
            } else {
                setError(error.response?.data?.detail || error.message || 'Activation failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 4A. FORGOT PASSWORD - Request OTP
    const handleForgotPasswordRequest = async (e) => {
        e.preventDefault();

        if (!email) {
            setError('Please enter your email address');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            // First, find schools for this email
            const response = await axios.post(`${API}/password/forgot/find-schools`, {
                email: email.toLowerCase()
            });

            const schools = response.data.schools;

            if (schools.length === 1) {
                // If only one school, proceed directly
                await sendPasswordResetOTP(schools[0].id);
            } else {
                // Multiple schools, show selection
                setForgotPasswordSchools(schools);
                setStage('forgotPasswordSchoolSelection');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to send reset code');
            } else {
                setError(error.message || 'Failed to send reset code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const sendPasswordResetOTP = async (selectedSchoolId) => {
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await axios.post(`${API}/password/forgot`, {
                email: email.toLowerCase(),
                school_id: selectedSchoolId
            });

            // Store the school ID for later use in password reset
            setSchoolId(selectedSchoolId);
            setSuccessMessage('Password reset code sent to your email');
            setStage('resetPasswordOtp');
            startOtpTimer();
        } catch (error) {
            console.error('Send OTP error:', error);
            const errorDetail = error.response?.data?.detail;
            if (typeof errorDetail === 'string') {
                setError(errorDetail);
            } else if (Array.isArray(errorDetail)) {
                setError(errorDetail.map(err => typeof err === 'string' ? err : err.msg || 'Validation error').join(', '));
            } else if (errorDetail && typeof errorDetail === 'object') {
                setError(errorDetail.msg || 'Failed to send reset code');
            } else {
                setError(error.message || 'Failed to send reset code');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 4B. FORGOT PASSWORD - Reset with OTP
    const handlePasswordReset = async (e) => {
        e.preventDefault();
        const otpString = otp.join('');

        // Validation
        if (otpString.length !== 6) {
            setError('Please enter the complete 6-digit verification code');
            return;
        }

        if (otpTimeLeft <= 0) {
            setError('Reset code has expired. Please request a new one.');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            if (!schoolId) {
                setError('School information is missing. Please try the forgot password process again.');
                return;
            }

            await axios.post(`${API}/password/reset`, {
                email: email.toLowerCase(),
                otp: parseInt(otpString, 10),
                new_password: newPassword,
                school_id: schoolId
            });

            // Clear timer
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);

            setSuccessMessage('Password reset successful! You can now log in with your new password.');
            setStage('login');
            setPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setOtp(Array(6).fill(''));
        } catch (error) {
            console.error('Password reset error:', error);

            if (error.response?.status === 400) {
                setError('That code is invalid or expired. Request a new one.');
            } else if (error.response?.status === 404) {
                setError('Account not found. Please check your email and school.');
            } else {
                setError(error.response?.data?.detail || error.message || 'Reset failed');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Store login tokens and redirect
    const handleLoginSuccess = (data) => {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('access_token', data.access_token); // Alternative key
        localStorage.setItem('refresh_token', data.refresh_token || '');
        localStorage.setItem('school_id', data.school_id || '');
        localStorage.setItem('school_name', data.school_name || '');
        localStorage.setItem('user_role', 'student');
        localStorage.setItem('user_email', email.toLowerCase());

        // Navigate to dashboard
        navigate('/HomePage');
    };

    // OTP INPUT HANDLING
    const handleOtpChange = (value, idx) => {
        if (!/^[0-9]?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[idx] = value;
        setOtp(newOtp);

        // Auto-focus to next input if value entered
        if (value && idx < otp.length - 1) {
            otpRefs.current[idx + 1].focus();
        }
    };

    const handleOtpKeyDown = (e, idx) => {
        // Handle backspace for empty inputs
        if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
            otpRefs.current[idx - 1].focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').trim();

        // Check if pasted content is a 6-digit number
        if (/^\d{6}$/.test(pastedData)) {
            const digits = pastedData.split('');
            setOtp(digits);

            // Focus the last input
            otpRefs.current[5].focus();
        }
    };

    // UI NAVIGATION HELPERS
    const goToForgotPassword = () => {
        setStage('resetPassword');
        setError('');
        setSuccessMessage('');
    };

    const goToLogin = () => {
        setStage('login');
        setError('');
        setSuccessMessage('');

        // Clear any active timers
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };

    // -------------------- RENDER UI --------------------

    // Create an array of background slide images
    const backgroundSlides = [
        'https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/13794/SM496503.jpg',
        'https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/12135/SM377070.jpg',
        'https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/19549/SM940390.jpg',
        'https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14229/SM523032.jpg',
        'https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/17072/SM743851.jpg',
        'https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/14550/SM543105.jpg'
    ];

    return (
        <div className="min-h-screen text-gray-800">
            {/* Background layers */}
            <div className="bg-slideshow" aria-hidden="true">
                {backgroundSlides.map((imageUrl, index) => (
                    <div
                        key={index}
                        className="bg-slide"
                        style={{ backgroundImage: `url('${imageUrl}')` }}
                    />
                ))}
            </div>
            <div className="gradient-overlay" aria-hidden="true"></div>
            <div className="top-white-fade" aria-hidden="true"></div>

            {/* Header */}
            <header className="relative z-10">
                <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                    {/* Logo - Now using Holistic Evaluator logo */}
                    <div className="flex items-center gap-4 animate-slideInLeft">
                        <img
                            src={heLogo}
                            alt="Holistic Evaluator Logo"
                            className="h-20 md:h-24 w-auto object-contain drop-shadow"
                        />
                    </div>

                    {/* Tagline */}
                    <div className="hidden md:block text-right">
                        <p className="text-gray-900 text-base md:text-lg font-medium">Holistic Progress Card by PARAKH</p>
                        <p className="text-gray-700 text-sm">Performance Assessment, Review, and Analysis of Knowledge Holistic Development</p>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className="relative z-10">
                <section className="min-h-[calc(100vh-88px)] flex items-center justify-center px-4 pb-10">
                    <div className="w-full max-w-md">
                        {/* Login card */}
                        <div className="panel-glass soft-shadow rounded-2xl p-8 animate-fadeInUp">
                            {/* Line before Student Login */}
                            <div className="text-center mb-4">
                                <h3 className="text-base font-semibold text-orange-600">Empowering Students for #ViksitBharat2047.</h3>
                            </div>

                            {/* Student icon + strip */}
                            <div className="text-center mb-6">
                                <img
                                    src="https://www.svgrepo.com/show/398447/teacher.svg"
                                    alt="Student Icon"
                                    className="mx-auto w-[108px] h-[108px] mb-0"
                                />
                                <div className="inline-block bg-blue-600 text-white px-5 py-1.5 rounded-full shadow -mt-1">
                                    <span className="text-[15px] font-semibold">
                                        {stage === 'login' && 'Student Login'}
                                        {stage === 'schoolSelection' && 'Select Your School'}
                                        {stage === 'activationSetup' && 'Account Activation'}
                                        {stage === 'resetPassword' && 'Reset Your Password'}
                                        {stage === 'forgotPasswordSchoolSelection' && 'Select Your School'}
                                        {stage === 'resetPasswordOtp' && 'Enter Verification Code'}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {stage === 'login' && 'Enter your credentials to access your progress card'}
                                    {stage === 'schoolSelection' && 'You have access to multiple schools. Please select one.'}
                                    {stage === 'activationSetup' && 'Enter your temporary password and choose a new password'}
                                    {stage === 'resetPassword' && 'We\'ll send a verification code to your email'}
                                    {stage === 'forgotPasswordSchoolSelection' && 'Select the school where you want to reset your password'}
                                    {stage === 'resetPasswordOtp' && 'Enter the code and your new password'}
                                </p>
                            </div>

                            {/* Error and Success Messages */}
                            {error && (
                                <div id="message" className="mb-6 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-start gap-2">
                                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {successMessage && (
                                <div id="message" className="mb-6 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm flex items-start gap-2">
                                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <span>{successMessage}</span>
                                </div>
                            )}

                            {/* 1. LOGIN FORM */}
                            {stage === 'login' && (
                                <form onSubmit={handleLogin} className="space-y-5">
                                    {/* Username/Email */}
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Mail className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type="email"
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your email"
                                                autoComplete="email"
                                            />
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your password"
                                                autoComplete="current-password"
                                            />
                                            <button
                                                type="button"
                                                aria-label="Toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                            >
                                                {showPassword ?
                                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                    : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Controls */}
                                    <div className="flex items-center justify-between text-sm">
                                        <label className="inline-flex items-center gap-2 select-none">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                                                id="remember-me"
                                                name="remember-me"
                                            />
                                            <span className="text-gray-700">Remember me</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="text-orange-600 hover:text-orange-700 font-medium"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition disabled:opacity-70 disabled:hover:bg-orange-600 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                    strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <LogIn className="h-5 w-5" />
                                        )}
                                        {isLoading ? 'Signing in...' : 'Sign In'}
                                    </button>

                                    {/* Extra text */}
                                    <div className="mt-6 text-center">
                                        <p className="text-sm text-gray-600">
                                            Don't have an account?{' '}
                                            <button type="button" className="font-medium text-orange-600 hover:text-orange-500">
                                                Contact Administrator
                                            </button>
                                        </p>
                                    </div>
                                </form>
                            )}

                            {/* 2. SCHOOL SELECTION */}
                            {stage === 'schoolSelection' && (
                                <div className="space-y-4">
                                    <div
                                        className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                                        Your account is associated with multiple schools. Please select the school you want to
                                        access.
                                    </div>

                                    <div className="space-y-2">
                                        {availableSchools.map(school => (
                                            <button
                                                key={school.id}
                                                onClick={() => completeLogin(school.id)}
                                                disabled={isLoading}
                                                className="w-full flex flex-col p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all text-left"
                                            >
                                                <span className="font-semibold text-gray-800">{school.name}</span>
                                                {school.domain && (
                                                    <span className="text-xs text-gray-500">{school.domain}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={goToLogin}
                                        disabled={isLoading}
                                        className="flex items-center justify-center text-orange-600 hover:text-orange-700 mt-4"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-1" />
                                        Back to login
                                    </button>
                                </div>
                            )}

                            {/* 3. ACTIVATION - TEMP PASSWORD + NEW PASSWORD (No OTP) */}
                            {stage === 'activationSetup' && (
                                <form onSubmit={handleActivation} className="space-y-6">
                                    <div
                                        className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700 flex items-start gap-2">
                                        <Shield className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-medium mb-1">First-time login</p>
                                            <p>Enter your temporary password and choose a new password to activate your account.</p>
                                        </div>
                                    </div>

                                    {!prefilledEmail && (
                                        <div className="space-y-2 focus-lift transition-transform duration-200">
                                            <label htmlFor="activation-email"
                                                className="block text-sm font-medium text-gray-700">
                                                Email
                                            </label>
                                            <div className="relative">
                                                <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                    <Mail className="h-5 w-5 text-gray-400" />
                                                </span>
                                                <input
                                                    type="email"
                                                    id="activation-email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                    placeholder="Enter your email"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="temp-password" className="block text-sm font-medium text-gray-700">
                                            Temporary Password
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="temp-password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your temporary password"
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ?
                                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                    : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                                            </button>
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">This is the temporary password provided to you
                                            by your administrator.</p>
                                    </div>

                                    {/* New Password */}
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                id="new-password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Create a new password"
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ?
                                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                    : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="confirm-password"
                                            className="block text-sm font-medium text-gray-700">
                                            Confirm Password
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                id="confirm-password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Confirm your new password"
                                            />
                                        </div>
                                    </div>

                                    {/* Password Requirements */}
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
                                        <ul className="space-y-1">
                                            <li className={`text-xs flex items-center ${passwordStrength.length ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.length ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                At least 8 characters long
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.uppercase ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                At least one uppercase letter
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.lowercase ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.lowercase ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                At least one lowercase letter
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.number ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.number ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                At least one number
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.special ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.special ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                At least one special character
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.match ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.match ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                Passwords match
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={goToLogin}
                                            className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {isLoading ? (
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                        strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            ) : null}
                                            {isLoading ? 'Activating...' : 'Activate Account'}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* 5. FORGOT PASSWORD REQUEST */}
                            {stage === 'resetPassword' && (
                                <form onSubmit={handleForgotPasswordRequest} className="space-y-6">
                                    <div
                                        className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm text-blue-700">
                                        <p>Enter your email address to find your account and reset your password.</p>
                                    </div>

                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Mail className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type="email"
                                                id="reset-email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={goToLogin}
                                            className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back to Login
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {isLoading && (
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                        strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            )}
                                            {isLoading ? 'Finding Account...' : 'Continue'}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* FORGOT PASSWORD SCHOOL SELECTION */}
                            {stage === 'forgotPasswordSchoolSelection' && (
                                <div className="space-y-4">
                                    <div
                                        className="mb-6 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                                        We found your account in multiple schools. Please select the school where you want to reset your password.
                                    </div>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {forgotPasswordSchools.map(school => (
                                            <div
                                                key={school.id}
                                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-orange-500 hover:bg-orange-50 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'border-gray-200'
                                                    }`}
                                                onClick={() => !isLoading && sendPasswordResetOTP(school.id)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <Shield size={20} className="text-orange-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h3 className="font-medium text-gray-800">{school.name}</h3>
                                                        <p className="text-sm text-gray-500">Click to reset password for this school</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setStage('resetPassword')}
                                        disabled={isLoading}
                                        className="w-full mt-6 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
                                    >
                                        <ArrowLeft className="h-4 w-4 mr-2" />
                                        Back to Email Entry
                                    </button>
                                </div>
                            )}

                            {/* 6. RESET PASSWORD WITH OTP */}
                            {stage === 'resetPasswordOtp' && (
                                <form onSubmit={handlePasswordReset} className="space-y-6">
                                    <div className="mb-2 text-center">
                                        <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                                            <Clock className="h-4 w-4" />
                                            <span className="text-sm font-medium">
                                                Code expires in {formatTime(otpTimeLeft)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* OTP Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                                            Enter the 6-digit verification code
                                        </label>
                                        <div className="flex justify-center space-x-2" onPaste={handleOtpPaste}>
                                            {otp.map((digit, idx) => (
                                                <input
                                                    key={idx}
                                                    ref={(el) => (otpRefs.current[idx] = el)}
                                                    type="text"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                                                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                                    className="w-10 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* New Password */}
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-new-password"
                                            className="block text-sm font-medium text-gray-700">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                id="reset-new-password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Create a new password"
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-2 grid place-items-center px-2 rounded-md hover:bg-gray-100 transition"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ?
                                                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                                    : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="space-y-2 focus-lift transition-transform duration-200">
                                        <label htmlFor="reset-confirm-password"
                                            className="block text-sm font-medium text-gray-700">
                                            Confirm Password
                                        </label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-3 grid place-items-center">
                                                <Lock className="h-5 w-5 text-gray-400" />
                                            </span>
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                id="reset-confirm-password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="w-full pl-10 pr-3 py-3 rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                                                placeholder="Confirm your new password"
                                            />
                                        </div>
                                    </div>

                                    {/* Password Requirements */}
                                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                        <p className="text-sm font-medium text-gray-700 mb-2">Password requirements:</p>
                                        <ul className="space-y-1">
                                            <li className={`text-xs flex items-center ${passwordStrength.length ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.length ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                At least 8 characters long
                                            </li>
                                            <li className={`text-xs flex items-center ${passwordStrength.match ? 'text-green-600' : 'text-gray-500'}`}>
                                                {passwordStrength.match ? (
                                                    <CheckCircle size={12} className="mr-1 flex-shrink-0" />
                                                ) : (
                                                    <span className="h-3 w-3 mr-1 flex-shrink-0" />
                                                )}
                                                Passwords match
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="flex space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => setStage('resetPassword')}
                                            className="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200"
                                        >
                                            <ArrowLeft className="h-4 w-4 mr-2" />
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className="flex-1 flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            {isLoading && (
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                                        strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            )}
                                            {isLoading ? 'Resetting...' : 'Reset Password'}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Compliance marquee */}
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="marquee-outer text-center text-xs md:text-sm font-medium py-1">
                                    <div className="marquee-track">
                                        <div className="marquee-chunk">
                                            We are aligned with <strong>National Education Policy</strong>, <strong>National Curriculum Framework</strong>, <strong>Holistic Progress Card Assessment Framework</strong>.
                                        </div>
                                        <div className="marquee-chunk">
                                            We are aligned with <strong>National Education Policy</strong>, <strong>National Curriculum Framework</strong>, <strong>Holistic Progress Card Assessment Framework</strong>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default Login;